import React, { useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Loader2, Coins, RefreshCw } from 'lucide-react';

// Wave 3.99e — Super Admin only dialog. Wraps
// POST /api/orders/admin/backfill-gold-from-cad with dry-run and apply
// modes plus optional company scoping.
//
// Business context:
//   Historic orders where CAM was confirmed BEFORE the CAM→auto-gold hook
//   shipped (Wave 3.96) may still be missing a Gold Requirement derived
//   from CAD weight. Deploying new code alone does not heal them.
//   This tool executes the safe, idempotent backfill helper so the
//   operator can heal these orders in one click.
//
// Safety:
//   - Super Admin only (backend enforces).
//   - Two-step apply: user must first dry-run, then confirm before writing.
//   - Backfill only ADDS missing grams; never reduces or mutates
//     existing fulfilments.

export function GoldBackfillDialog({ open, onOpenChange, companies = [] }) {
  const [companyId, setCompanyId] = useState('__all__');
  const [applyChanges, setApplyChanges] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const companyOptions = useMemo(
    () => [
      { value: '__all__', label: 'All companies' },
      ...companies.map((c) => ({ value: c.id, label: c.name })),
    ],
    [companies],
  );

  const reset = () => {
    setCompanyId('__all__');
    setApplyChanges(false);
    setReport(null);
  };

  const runBackfill = async (forceApply) => {
    setBusy(true);
    setReport(null);
    try {
      // Backend signature: POST /orders/admin/backfill-gold-from-cad
      //   query params: dry_run (bool), company_id (optional string)
      const params = {
        dry_run: !forceApply,
      };
      if (companyId && companyId !== '__all__') {
        params.company_id = companyId;
      }
      const { data } = await api.post('/orders/admin/backfill-gold-from-cad', null, { params });
      setReport({ ...data, apply_changes: !!forceApply });
      if (forceApply) {
        if ((data?.updated || 0) > 0) {
          toast.success(`Gold Requirement healed on ${data.updated} order(s).`);
        } else {
          toast.success('Nothing to heal — every CAM-confirmed order already has sufficient Gold Requirement.');
        }
      } else {
        if ((data?.updated || 0) === 0) {
          toast.success('Dry-run: no orders need healing.');
        } else {
          toast(`Dry-run: ${data.updated} order(s) would be healed.`, { icon: '⚠️' });
        }
      }
    } catch (e) {
      toast.error(extractError(e, 'Gold backfill failed'));
    } finally {
      setBusy(false);
    }
  };

  const onRunClick = () => {
    if (applyChanges) {
      setConfirmOpen(true);
    } else {
      runBackfill(false);
    }
  };

  const onConfirmApply = async () => {
    setConfirmOpen(false);
    await runBackfill(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) reset();
          onOpenChange(v);
        }}
      >
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="gold-backfill-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins size={18} className="text-amber-600" /> Gold Requirement Backfill from CAD
            </DialogTitle>
            <DialogDescription>
              For every order where <span className="font-medium">CAM is confirmed</span> but the
              gold requirement lines are short of the CAD weight, this tool adds the missing grams
              as a new Gold Requirement line. Dry-run reports what would change without writing.
              Apply mode writes the repair to the database.
              <br />
              <span className="text-[11px] text-slate-500">
                Safe &amp; idempotent — re-running after apply will report zero updates. Backfill
                never reduces existing requirements or touches fulfillments.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Company scope</Label>
              <Select value={companyId} onValueChange={setCompanyId}>
                <SelectTrigger data-testid="gold-backfill-company"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {companyOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">
                Choose a specific company to scope the sweep, or leave as “All companies” to run
                portal-wide.
              </div>
            </div>

            <div className="col-span-2 flex items-center justify-between rounded-md border border-[var(--divider)] bg-[var(--paper-warm)] px-3 py-2 mt-1">
              <div className="flex items-center gap-2">
                <AlertTriangle
                  size={16}
                  className={applyChanges ? 'text-amber-600' : 'text-slate-400'}
                />
                <div>
                  <div className="text-sm font-medium text-slate-800">Apply changes</div>
                  <div className="text-[11px] text-slate-500">
                    Off = dry-run (safe, read-only preview). On = writes new Gold Requirement lines
                    where CAD &gt; current gold required.
                  </div>
                </div>
              </div>
              <Switch
                checked={applyChanges}
                onCheckedChange={setApplyChanges}
                data-testid="gold-backfill-apply-toggle"
              />
            </div>
          </div>

          {report && <ReportPanel report={report} />}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="gold-backfill-close">
              Close
            </Button>
            <Button
              onClick={onRunClick}
              disabled={busy}
              className="luxury-button"
              data-testid="gold-backfill-run"
            >
              {busy ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Running…</>
              ) : applyChanges ? (
                <><RefreshCw size={14} className="mr-1.5" /> Run &amp; Apply</>
              ) : (
                <><RefreshCw size={14} className="mr-1.5" /> Run Dry-Run</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="gold-backfill-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" /> Confirm write to database
            </DialogTitle>
            <DialogDescription>
              This will add missing grams as a new Gold Requirement line for each CAM-confirmed
              order where CAD &gt; current gold required. A system comment is posted on every
              affected order for audit. The operation is safe and idempotent, but it does write
              to the shared database. Run a dry-run first if you haven&apos;t.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="gold-backfill-cancel-apply">
              Cancel
            </Button>
            <Button
              onClick={onConfirmApply}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="gold-backfill-confirm-apply"
            >
              Yes, apply backfill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ReportPanel({ report }) {
  const scanned = report?.scanned || 0;
  const updated = report?.updated || 0;
  const skippedNoCad = report?.skipped_no_cad_weight || 0;
  const skippedNoPurity = report?.skipped_no_purity || 0;
  const skippedSufficient = report?.skipped_already_sufficient || 0;
  const failed = report?.failed || 0;
  const details = Array.isArray(report?.details) ? report.details : [];
  const truncated = !!report?.details_truncated;
  const clean = updated === 0 && failed === 0;

  return (
    <div className="mt-4 space-y-3" data-testid="gold-backfill-report">
      <div className="flex items-center gap-2 flex-wrap">
        {clean ? (
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 size={12} className="mr-1" /> No orders need healing
          </Badge>
        ) : (
          <Badge className="bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle size={12} className="mr-1" /> Healing candidates found
          </Badge>
        )}
        <Badge variant="outline" className="text-xs">
          {report.apply_changes ? 'Applied' : 'Dry-run'}
        </Badge>
        {failed > 0 && (
          <Badge className="bg-rose-50 text-rose-700 border border-rose-200 text-xs">
            {failed} failure(s)
          </Badge>
        )}
      </div>

      <Card className="p-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
          <SummaryStat label="Orders scanned" value={scanned} />
          <SummaryStat
            label={report.apply_changes ? 'Orders healed' : 'Would heal'}
            value={updated}
            highlight={updated > 0}
          />
          <SummaryStat label="Skipped: no CAD" value={skippedNoCad} />
          <SummaryStat label="Skipped: no purity" value={skippedNoPurity} />
          <SummaryStat label="Already sufficient" value={skippedSufficient} />
        </div>
      </Card>

      {details.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="ledger-table max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--paper-warm)] z-10">
                <tr>
                  <th className="text-left px-2 py-1">Order No</th>
                  <th className="text-right px-2 py-1">CAD (g)</th>
                  <th className="text-left px-2 py-1">Purity</th>
                  <th className="text-right px-2 py-1">Delta added (g)</th>
                </tr>
              </thead>
              <tbody>
                {details.map((row) => (
                  <tr key={row.order_id}>
                    <td className="px-2 py-1 font-mono text-[11px]">{row.order_no || row.order_id?.slice(0, 8)}</td>
                    <td className="px-2 py-1 text-right font-mono tabular-nums">{fmtG(row.cad_weight_grams)}</td>
                    <td className="px-2 py-1 text-slate-600">{row.purity_label || '—'}</td>
                    <td className="px-2 py-1 text-right font-mono tabular-nums text-amber-700 font-medium">
                      +{fmtG(row.delta_grams)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {truncated && (
            <div className="text-[11px] text-slate-500 px-3 py-1.5 border-t border-[var(--divider)]">
              Showing first {details.length} orders — the full list is longer. Counts above capture the totals.
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function SummaryStat({ label, value, highlight }) {
  return (
    <div className="rounded-md border border-[var(--divider)] px-2 py-1">
      <div className={`text-lg font-semibold tabular-nums ${highlight ? 'text-amber-700' : 'text-slate-800'}`}>{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}

function fmtG(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return Number(v).toFixed(3);
}
